import {useTenant} from '@store/settings'
import React, {useEffect, useState} from 'react'
import {useSelector} from 'react-redux'
import SwaggerUI from 'swagger-ui-react'
import 'swagger-ui-react/swagger-ui.css'
import YAML from 'yaml'
import {useDomain} from 'contexts/DomainContext'

const hideAuthorizeButton = `
  .swagger-ui .auth-wrapper { display: none !important; }
`

const SERVER_URL = process.env.REACT_APP_BACKEND_URL

const ApiDocs = () => {
  const user = useSelector(state => state.auth.user)
  const [tenant, setTenant] = useTenant(user)
  const [spec, setSpec] = useState(null)
  const {terms} = useDomain()

  useEffect(() => {
    // Determine which OpenAPI spec to load based on user's tenant domain
    const getSpecUrl = () => {
      if (user?.tid && tenant?.domain && tenant.domain !== 'system') {
        // Try to load domain-specific spec first
        return `/openapi-${tenant.domain}.yaml`
      }
      return '/openapi.yaml'
    }

    const loadSpec = async url => {
      try {
        const response = await fetch(url)
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`)
        }
        return await response.text()
      } catch (error) {
        // Fallback to base spec if domain-specific spec fails
        if (url !== '/openapi.yaml') {
          console.warn(`Failed to load ${url}, falling back to base spec:`, error)
          return await fetch('/openapi.yaml').then(res => res.text())
        }
        throw error
      }
    }

    loadSpec(getSpecUrl()).then(yamlText => {
      const spec = YAML.parse(yamlText)
      spec.servers = [{url: SERVER_URL}]

      // Update parameter descriptions based on domain
      if (spec.components?.parameters && terms) {
        const parameterMappings = {
          t_id: {description: `Filter by ${terms.tenant.toLowerCase()} ID (exact match)`},
          tid: {description: `Filter by ${terms.tenant.toLowerCase()} ID (exact match)`},
          t_name: {description: `Filter by ${terms.tenant.toLowerCase()} name (prefix match)`},
          s_id: {description: `Filter by ${terms.site.toLowerCase()} ID (exact match)`},
          sid: {description: `Filter by ${terms.site.toLowerCase()} ID (exact match)`},
          s_name: {description: `Filter by ${terms.site.toLowerCase()} name (prefix match)`},
          g_id: {description: `Filter by ${terms.gateway.toLowerCase()} ID (exact match)`},
          gid: {description: `Filter by ${terms.gateway.toLowerCase()} ID (exact match)`},
          g_name: {description: `Filter by ${terms.gateway.toLowerCase()} name (prefix match)`},
          ca_id: {description: `Filter by ${terms.camera.toLowerCase()} ID (exact match)`},
          caid: {description: `Filter by ${terms.camera.toLowerCase()} ID (exact match)`},
          ca_name: {description: `Filter by ${terms.camera.toLowerCase()} name (prefix match)`},
          tg_id: {description: `Filter by ${terms.target.toLowerCase()} ID (exact match)`},
          tgid: {description: `Filter by ${terms.target.toLowerCase()} ID (exact match)`},
          tg_name: {description: `Filter by ${terms.target.toLowerCase()} name (prefix match)`}
        }

        // Update parameter descriptions
        Object.keys(parameterMappings).forEach(paramName => {
          if (spec.components.parameters[paramName]) {
            spec.components.parameters[paramName].description = parameterMappings[paramName].description
          }
        })

        // Update schema descriptions
        const schemaUpdates = {
          Site: {
            description: `Complete ${terms.site.toLowerCase()} information with comprehensive field descriptions, monitoring configuration, and geographic location.`,
            properties: {
              name: {description: `Human-readable ${terms.site.toLowerCase()} name.`}
            }
          },
          Gateway: {
            description: `Complete ${terms.gateway.toLowerCase()} information with comprehensive field descriptions, hardware identification, and ${terms.site.toLowerCase()} relationship management.`,
            properties: {
              name: {description: `Human-readable ${terms.gateway.toLowerCase()} name.`}
            }
          },
          Camera: {
            description: `Complete ${terms.camera.toLowerCase()} information with hardware identification, ${terms.gateway.toLowerCase()} assignment, and monitoring configuration.`,
            properties: {
              name: {description: `${terms.camera} name (unique within ${terms.gateway.toLowerCase()})`}
            }
          },
          Target: {
            description: `Complete ${terms.target.toLowerCase()} information with thermal monitoring parameters, ${terms.camera.toLowerCase()} assignment, and environmental configuration.`,
            properties: {
              name: {description: `${terms.target} name (unique within ${terms.camera.toLowerCase()})`}
            }
          }
        }

        // Apply schema updates
        Object.keys(schemaUpdates).forEach(schemaName => {
          if (spec.components.schemas[schemaName]) {
            if (schemaUpdates[schemaName].description) {
              spec.components.schemas[schemaName].description = schemaUpdates[schemaName].description
            }
            if (schemaUpdates[schemaName].properties) {
              Object.keys(schemaUpdates[schemaName].properties).forEach(propName => {
                if (spec.components.schemas[schemaName].properties?.[propName]) {
                  spec.components.schemas[schemaName].properties[propName].description =
                    schemaUpdates[schemaName].properties[propName].description
                }
              })
            }
          }
        })

        // Only update operation summaries if we're not using a pre-generated domain spec
        if (!user?.tid || !tenant?.domain || tenant.domain === 'system') {
          const operationUpdates = {
            '/sites': {
              get: {summary: `List ${terms.sites.toLowerCase()} with role-based access control`},
              post: {summary: `Create new ${terms.site.toLowerCase()} with tenant assignment`}
            },
            '/gateways': {
              get: {
                summary: `List ${terms.gateways.toLowerCase()} with role-based access control and hardware identification`
              },
              post: {
                summary: `Create new ${terms.gateway.toLowerCase()} with ${terms.site.toLowerCase()} assignment and hardware identification`
              }
            },
            '/cameras': {
              get: {
                summary: `List ${terms.cameras.toLowerCase()} with role-based access control and hardware identification`
              },
              post: {
                summary: `Create new ${terms.camera.toLowerCase()} with ${terms.gateway.toLowerCase()} assignment and hardware identification`
              }
            },
            '/targets': {
              get: {
                summary: `List ${terms.targets.toLowerCase()} with role-based access control and thermal monitoring parameters`
              },
              post: {
                summary: `Create new ${terms.target.toLowerCase()} with ${terms.camera.toLowerCase()} assignment and thermal monitoring parameters`
              }
            }
          }

          // Apply operation updates
          Object.keys(operationUpdates).forEach(path => {
            if (spec.paths[path]) {
              Object.keys(operationUpdates[path]).forEach(method => {
                if (spec.paths[path][method]) {
                  Object.assign(spec.paths[path][method], operationUpdates[path][method])
                }
              })
            }
          })
        }
      }

      if (user.role === 'sysadmin' || user.role === 'sysadminRO') {
        // Patch tid and t_id default value
        if (spec.components?.parameters?.tid?.schema) {
          spec.components.parameters.tid.schema.default = tenant?.id
          spec.components.parameters.tid.schema.example = tenant?.id
        }
        if (spec.components?.parameters?.t_id?.schema) {
          spec.components.parameters.t_id.schema.default = tenant?.id
          spec.components.parameters.t_id.schema.example = tenant?.id
        }
      } else {
        // Remove tid parameter from every operation so it's hidden in UI
        for (const path in spec.paths) {
          const methods = spec.paths[path]
          for (const method in methods) {
            const op = methods[method]
            if (op.parameters) {
              op.parameters = op.parameters.filter(
                p => !(p['$ref'] && (p['$ref'].endsWith('/tid') || p['$ref'].endsWith('/t_id')))
              )
            }
          }
        }
      }
      setSpec(spec)
    })
  }, [tenant?.id, terms])

  if (!spec) return <div>Loading API docs...</div>

  return (
    <>
      <style>{hideAuthorizeButton}</style>
      <SwaggerUI
        spec={spec}
        docExpansion="none" // 👈 collapse all by default
        requestInterceptor={req => {
          req.credentials = 'include'
          const token = localStorage.getItem('token')
          if (token) {
            req.headers['Authorization'] = `Bearer ${token}`
          }
          return req
        }}
      />
    </>
  )
}

export default ApiDocs
